import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { validate } from "capitalrx-components-2.0";
import { Dropdown, Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import {
  Color,
  Icon,
  LoadingGroup,
  LoadingSkeleton,
  Message,
  MessageAppearance,
  Spinner,
  Tooltip,
} from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Paragraph, Subheading, Underlined } from "capitalrx-components-2.0/lib/typography";

import { useEstimateMedicarePrescriptionPaymentPlanPayments } from "@/App/api/medicarePrescriptionPaymentPlan";
import { useListMemberAccumulationsQuery, useListMemberMedicareAmountQuery } from "@/App/api/member";
import { NoResults, ScreenReader, ThemedSubmitButton } from "@/App/components";
import { useIsParticipatingInMedicarePrescriptionPlan } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { MedicarePrescriptionPaymentPlanEstimateRequest } from "@/App/types/medicarePrescriptionPaymentPlan";
import { formatDollars, subtract } from "@/App/utils/utils";
import { PaymentBarChart } from "./PaymentBarChart";
import { PaymentTable } from "./PaymentTable";
import { MONTHS } from "./utils";
import { MemberEligibilityContext } from "@/App/context";

const BottomAlignedStack = styled(Stack)`
  height: 100%;
  justify-content: flex-end;
`;

interface PaymentCalculatorForm {
  readonly expectedMonthlyCosts: number;
  readonly beginningMonth: string;
  readonly year: number;
}

interface PaymentCalculatorProps {
  readonly onOptIn: () => void;
}

const MessageBoxTypeAppearance: MessageAppearance = {
  icon: "info-circle",
  backgroundColor: Color.Red[50],
  borderColor: Color.Red[200],
  iconColor: Color.Red[500],
};

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculatorTab";

export const PaymentCalculatorTab: FC<PaymentCalculatorProps> = ({ onOptIn }) => {
  const [threshold, setThreshold] = useState(0);
  const [estimatedPaymentsFilters, setEstimatedPaymentsFilters] = useState<MedicarePrescriptionPaymentPlanEstimateRequest>({
    year: 0,
    start_month: 0,
    true_out_of_pocket_per_month: "0",
    total_true_out_of_pocket_amount: 0,
  });

  const { t: translate } = useTranslation();

  const now = DateTime.now();
  const medicarePrescriptionPlanMinDate = DateTime.fromFormat("20250101", "yyyyLLdd");
  const { isCompressed, formGridSpan } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
    formGridSpan: {
      mobile: 12,
      tablet: 4,
      desktop: 2,
    },
  });

  const { data: memberAccumulations, isLoading: isLoadingAccums } = useListMemberAccumulationsQuery(true);
  // TODO: include medicare accumulations in here later (as of now 11/24, no medicare users will have medical claims)
  const { data: medicareAmounts, isLoading: isLoadingMedicareAmounts } = useListMemberMedicareAmountQuery();

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const availableYears = useMemo(() => {
    if (medicareAmounts) {
      return memberEligibility.mppp_plan_year_bounds.filter((x) => x.is_open).map((x) => x.plan_year);
    } else {
      return [];
    }
  }, [memberEligibility.mppp_plan_year_bounds, medicareAmounts]);

  const {
    data: estimatedPayments,
    isLoading: isLoadingEstimatedPayments,
    isError: isEstimationError,
  } = useEstimateMedicarePrescriptionPaymentPlanPayments(estimatedPaymentsFilters);
  const { isParticipating } = useIsParticipatingInMedicarePrescriptionPlan(2025);

  const paymentCalculatorForm = useForm<PaymentCalculatorForm>({
    defaultValues: {
      year: DateTime.max(now, medicarePrescriptionPlanMinDate).year,
      beginningMonth: DateTime.max(now, medicarePrescriptionPlanMinDate).month.toString(),
    },
  });
  const monthlyCost = paymentCalculatorForm.watch("expectedMonthlyCosts");
  const selectedYear = paymentCalculatorForm.watch("year");

  const remainingTroop = useMemo(() => {
    if (selectedYear === now.year) {
      if (memberAccumulations) {
        return subtract(
          memberAccumulations?.true_out_of_pocket?.individual_max_amount,
          memberAccumulations?.true_out_of_pocket?.individual_amount,
        );
      } else {
        return formatDollars(0);
      }
    } else {
      return formatDollars(threshold);
    }
  }, [memberAccumulations, selectedYear, threshold, now]);

  const anyPaymentExceedsExpected = useMemo(() => {
    if (estimatedPayments) {
      return !!estimatedPayments.results.find((payment) => {
        const troopAmount = new Decimal(payment?.estimation?.true_out_of_pocket_amount || 0);
        const billedAmount = new Decimal(payment?.estimation?.billed_amount || 0);
        return troopAmount.greaterThan(0) && billedAmount.greaterThan(monthlyCost);
      });
    } else {
      return false;
    }
  }, [estimatedPayments]);

  // change the troop threshold on year change or medicare amounts changing
  useEffect(() => {
    const matchingMedicareAmount = (medicareAmounts?.results || []).find((amnt) => {
      const amntYear = DateTime.fromISO(amnt.medicare_amount.effective_date, {
        zone: "utc",
      }).year;
      return amntYear === selectedYear;
    });
    if (matchingMedicareAmount) {
      setThreshold(matchingMedicareAmount?.medicare_amount?.true_out_of_pocket_maximum);
    }
  }, [selectedYear, medicareAmounts]);

  const onSubmit = useCallback(
    async (formData: PaymentCalculatorForm) => {
      logEvent(ANALYTIC_EVENTS.MPPP.CALCULATOR.calculate);
      const totalTroop = selectedYear === now.year ? +(memberAccumulations?.true_out_of_pocket?.individual_amount || 0) : 0;

      setEstimatedPaymentsFilters({
        year: formData.year,
        true_out_of_pocket_per_month: formData.expectedMonthlyCosts.toString(),
        start_month: +formData.beginningMonth,
        total_true_out_of_pocket_amount: totalTroop,
      });
    },
    [memberAccumulations, selectedYear, now],
  );

  return (
    <Stack>
      <Subheading textAlign={isCompressed ? "center" : "left"}>
        {translate(`${TRANSLATION_PATH}.paymentCalculator`)}
      </Subheading>
      <Body>
        {translate(`${TRANSLATION_PATH}.calculatorToolHelpText`)}{" "}
        {!isParticipating && (
          <Trans
            i18nKey={`${TRANSLATION_PATH}.isThisPlanGoodFitForYou`}
            components={{ GoToOptIn: (
              <Underlined style={{ cursor: "pointer" }}>
                <ScreenReader>{translate(`${TRANSLATION_PATH}.goToOptInTab`)}</ScreenReader>
                <span onClick={onOptIn}>{translate(`${TRANSLATION_PATH}.optIn`)}</span>
              </Underlined>
            ) }}
          />
        )}
      </Body>
      <Form form={paymentCalculatorForm} onSubmit={onSubmit} loading={isLoadingAccums || isLoadingMedicareAmounts}>
        <Grid>
          <Grid.Span columns={formGridSpan}>
            <BottomAlignedStack spacing="mini">
              <Paragraph>
                <Bold>{translate(`${TRANSLATION_PATH}.threshold`)} </Bold>
                <Tooltip
                  content={`${translate(`${TRANSLATION_PATH}.thresholdDescription`)} ${selectedYear}.`}
                >
                  {(ref) => (
                    <span ref={ref}>
                      <Icon size="small" name="info-circle" />
                    </span>
                  )}
                </Tooltip>
              </Paragraph>
              <LoadingGroup loading={isLoadingMedicareAmounts}>
                <LoadingGroup.Placeholder fallback={<LoadingSkeleton />}>
                  <Paragraph>{formatDollars(threshold)}</Paragraph>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </BottomAlignedStack>
          </Grid.Span>

          <Grid.Span columns={formGridSpan}>
            <BottomAlignedStack spacing="mini">
              <Paragraph>
                <Bold>{translate(`${TRANSLATION_PATH}.remainingTrOOP`)} </Bold>
                <Tooltip
                  content={`${translate(`${TRANSLATION_PATH}.remainingTrOPPDescription`)} ${selectedYear}.`}
                >
                  {(ref) => (
                    <span ref={ref}>
                      <Icon size="small" name="info-circle" />
                    </span>
                  )}
                </Tooltip>
              </Paragraph>
              <LoadingGroup loading={isLoadingAccums}>
                <LoadingGroup.Placeholder fallback={<LoadingSkeleton />}>
                  <Paragraph>{remainingTroop}</Paragraph>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </BottomAlignedStack>
          </Grid.Span>

          <Grid.Span columns={formGridSpan}>
            <Textbox.Field
              validator={validate.isNumeric}
              name="expectedMonthlyCosts"
              label={translate(`${TRANSLATION_PATH}.expectedMonthlyCosts`)}
              required
            />
          </Grid.Span>

          <Grid.Span columns={formGridSpan}>
            <Dropdown.Field
              required
              name="beginningMonth"
              label={translate(`${TRANSLATION_PATH}.beginningMonth`)}
              placeholder={translate(`${TRANSLATION_PATH}.select`)}
            >
              {Object.keys(MONTHS).map((monthNumber) => {
                const monthName = translate(`${TRANSLATION_PATH}.${MONTHS[+monthNumber].toLowerCase()}`);
                return (
                  <Dropdown.Option key={monthNumber} value={monthNumber}>
                    {monthName}
                  </Dropdown.Option>
                );
              })}
            </Dropdown.Field>
          </Grid.Span>

          <Grid.Span columns={formGridSpan}>
            <Dropdown.Field
              required
              name="year"
              label={translate(`${TRANSLATION_PATH}.year`)}
              placeholder={translate(`${TRANSLATION_PATH}.select`)}
            >
              {availableYears.map((year) => {
                return (
                  <Dropdown.Option key={year} value={year}>
                    {year}
                  </Dropdown.Option>
                );
              })}
            </Dropdown.Field>
          </Grid.Span>

          <Grid.Span columns={formGridSpan}>
            <BottomAlignedStack>
              <Shelf>
                <ThemedSubmitButton>
                  {translate(`${TRANSLATION_PATH}.calculate`)}
                </ThemedSubmitButton>
                <Shelf.Spacer />
              </Shelf>
            </BottomAlignedStack>
          </Grid.Span>
        </Grid>
      </Form>

      <LoadingGroup loading={isLoadingEstimatedPayments && !!estimatedPaymentsFilters.start_month && !!estimatedPaymentsFilters.year}>
        <LoadingGroup.Placeholder
          fallback={
            <Shelf alignment="center">
              <Spinner size="large" />
            </Shelf>
          }
        >
          <Message appearance={MessageBoxTypeAppearance} visible={anyPaymentExceedsExpected}>
            {translate(`${TRANSLATION_PATH}.anyPaymentExceedsExpectedNote`)}
          </Message>
          {!!estimatedPayments && <PaymentBarChart estimatedPayments={estimatedPayments.results.map((e) => e.estimation)} />}
          {!!estimatedPayments && (
            <PaymentTable monthlyCost={+monthlyCost} estimatedPayments={estimatedPayments.results.map((e) => e.estimation)} />
          )}
          {isEstimationError && <NoResults message={translate(`${TRANSLATION_PATH}.estimationErrorMessage`)} />}
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
